/**
 * "Include a picture of this page": what the person sees right now (the
 * visible part of the page, not the whole scroll), drawn in the browser with
 * html2canvas (already in the app for the PDF exports, loaded only when a
 * picture is taken), as a JPEG of at most MAX_BYTES.
 *
 * What the picture never shows (all done on html2canvas's copy of the page;
 * the real page is untouched):
 *   - the Feedback tab and form themselves (data-feedback-ui);
 *   - anything typed or chosen in a form: every text box, text area,
 *     editable area, file field and dropdown is drawn empty, placeholders
 *     too;
 *   - a part of a page marked data-feedback-private (the job card's capture
 *     log with guests' photos and names, the registration QR code): drawn
 *     as a plain grey box of the same size;
 *   - link tokens and other secrets in the page's text: any run of 24 or
 *     more token characters, a JWT, a URL's query string or fragment,
 *     key=value pairs naming a secret, email addresses (redact.js).
 * Anything that fails (a page html2canvas cannot draw, an image from another
 * site that taints the canvas, a picture still too large after shrinking, a
 * timeout) resolves to { ok: false } and the report goes without it.
 */
import { redactPageText } from "./redact.js";

export const MAX_BYTES = 560 * 1024;
export const MAX_WIDTH = 1280;
const TIMEOUT_MS = 10_000;

const TEXT_INPUT_SKIP = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "image", "hidden"]);

/** Empties every form field in the cloned page. */
export function blankFormFields(doc) {
  for (const el of doc.querySelectorAll("input")) {
    if (TEXT_INPUT_SKIP.has((el.getAttribute("type") || "text").toLowerCase())) continue;
    // A file input only takes "" (its chosen file's name goes with it).
    try { el.value = ""; } catch { /* not settable */ }
    el.setAttribute("value", "");
    el.removeAttribute("placeholder");
  }
  for (const el of doc.querySelectorAll("textarea")) {
    el.value = "";
    el.textContent = "";
    el.removeAttribute("placeholder");
  }
  for (const el of doc.querySelectorAll("select")) {
    for (const opt of el.querySelectorAll("option")) opt.textContent = "";
  }
  for (const el of doc.querySelectorAll("[contenteditable]:not([contenteditable='false'])")) {
    el.textContent = "";
  }
}

export const scrubSecretText = (text) => redactPageText(text);

/** Scrubs link tokens and secrets out of every text in the cloned page. */
export function scrubPageText(doc) {
  const view = doc.defaultView;
  if (!doc.body || typeof doc.createTreeWalker !== "function") return;
  const walker = doc.createTreeWalker(doc.body, view?.NodeFilter?.SHOW_TEXT ?? 4);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const next = scrubSecretText(n.nodeValue);
    if (next !== n.nodeValue) n.nodeValue = next;
  }
}

export const PRIVATE_SELECTOR = "[data-feedback-private]";

/** The size of each private part of the live page, in page order. */
export function privateSizes(doc) {
  return [...doc.querySelectorAll(PRIVATE_SELECTOR)].map((el) => {
    const r = el.getBoundingClientRect();
    return { width: Math.round(r.width), height: Math.round(r.height) };
  });
}

/** Replaces each private part of the clone with a grey box of its live size. */
export function maskPrivate(doc, sizes = []) {
  [...doc.querySelectorAll(PRIVATE_SELECTOR)].forEach((el, i) => {
    const size = sizes[i] || { width: 0, height: 0 };
    el.innerHTML = "";
    el.textContent = "Hidden in feedback pictures";
    Object.assign(el.style, {
      background: "#D9D6D1", color: "#6B6B6B", fontSize: "12px", display: "flex",
      alignItems: "center", justifyContent: "center", borderRadius: "6px",
      minHeight: `${Math.max(size.height, 24)}px`, width: size.width ? `${size.width}px` : el.style.width,
      overflow: "hidden",
    });
  });
}

export const isFeedbackUi = (el) => typeof el?.hasAttribute === "function" && el.hasAttribute("data-feedback-ui");

/** Base64 length → bytes. */
export const base64Bytes = (b64) => Math.floor((b64.length * 3) / 4) - (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);

function toJpeg(canvas, width, quality, doc) {
  let src = canvas;
  if (canvas.width > width) {
    src = doc.createElement("canvas");
    src.width = width;
    src.height = Math.round((canvas.height * width) / canvas.width);
    src.getContext("2d").drawImage(canvas, 0, 0, src.width, src.height);
  }
  const url = src.toDataURL("image/jpeg", quality);
  const b64 = url.slice(url.indexOf(",") + 1);
  return { b64, bytes: base64Bytes(b64), width: src.width, height: src.height };
}

/** Shrinks until it fits: { ok: true, base64, bytes } or { ok: false }. */
export function encodeWithinCap(canvas, doc = document) {
  for (const [width, quality] of [[MAX_WIDTH, 0.72], [1024, 0.6], [800, 0.5]]) {
    const out = toJpeg(canvas, width, quality, doc);
    if (out.b64 && out.bytes <= MAX_BYTES) return { ok: true, base64: out.b64, bytes: out.bytes, width: out.width, height: out.height };
  }
  return { ok: false, reason: "too-large" };
}

/** Everything done to html2canvas's copy of the page before it is drawn. */
export function prepareClone(doc, sizes) {
  maskPrivate(doc, sizes);
  blankFormFields(doc);
  scrubPageText(doc);
}

export async function capturePage({ win = window, loader = () => import("html2canvas"), inspectClone = null } = {}) {
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ ok: false, reason: "timeout" }), TIMEOUT_MS); });
  const work = (async () => {
    const html2canvas = (await loader()).default;
    const vw = win.innerWidth;
    const vh = win.innerHeight;
    const sizes = privateSizes(win.document);
    const canvas = await html2canvas(win.document.body, {
      backgroundColor: "#F5F3F0",
      logging: false,
      useCORS: true,
      allowTaint: false,
      scale: Math.min(1, MAX_WIDTH / Math.max(1, vw)),
      x: win.scrollX,
      y: win.scrollY,
      width: vw,
      height: vh,
      windowWidth: win.document.documentElement.clientWidth || vw,
      windowHeight: vh,
      ignoreElements: isFeedbackUi,
      onclone: (doc) => {
        prepareClone(doc, sizes);
        if (inspectClone) inspectClone(doc);
      },
    });
    return encodeWithinCap(canvas, win.document);
  })().catch(() => ({ ok: false, reason: "failed" }));
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
